import { useState } from 'react';
import { AlertCircle, CheckCircle, XCircle, Info, BookOpen } from 'lucide-react';

const SurveillanceMatrix = () => {
  const [autonomyLevel, setAutonomyLevel] = useState('');
  const [surveillanceType, setSurveillanceType] = useState('');
  const [implementation, setImplementation] = useState('');
  const [orgCulture, setOrgCulture] = useState('');
  const [surveillancePurpose, setSurveillancePurpose] = useState('');
  const [showResults, setShowResults] = useState(false);

  const autonomyLevels = [
    {
      value: 'low',
      label: 'Low Autonomy',
      description: 'Call centers, data entry, assembly line workers',
      examples: 'Scripted tasks, close supervision, minimal discretion'
    },
    {
      value: 'medium',
      label: 'Medium Autonomy',
      description: 'Technical support, administrative roles, customer service',
      examples: 'Structured guidelines with moderate discretion'
    },
    {
      value: 'high',
      label: 'High Autonomy',
      description: 'Software engineers, consultants, researchers, creative professionals',
      examples: 'Substantial process discretion with "tied autonomy" constraints'
    },
    {
      value: 'ultra',
      label: 'Ultra-High Autonomy',
      description: 'Senior management, entrepreneurs, independent contractors',
      examples: 'Near-complete discretion in strategic and executive roles'
    }
  ];

  const surveillanceTypes = [
    {
      value: 'outcome',
      label: 'Outcome-Focused',
      description: 'Tracks deliverables and final results without process observation',
      impact: 'Lowest invasiveness - aligns with professional norms'
    },
    {
      value: 'mixed',
      label: 'Mixed Monitoring',
      description: 'Combines outcome metrics with selective process indicators',
      impact: 'Moderate invasiveness - depends on implementation'
    },
    {
      value: 'process',
      label: 'Process-Focused',
      description: 'Keystroke logging, screen capture, activity logs, time tracking',
      impact: 'High invasiveness - violates professional autonomy norms'
    },
    {
      value: 'pervasive',
      label: 'Pervasive Surveillance',
      description: 'Comprehensive monitoring: webcam, audio, biometric tracking',
      impact: 'Severe invasiveness - creates critical CI violations'
    }
  ];

  const implementationTypes = [
    {
      value: 'participatory',
      label: 'Transparent & Participatory',
      description: 'Collaborative design with employee voice in policy formation',
      detail: 'Clear communication + employee input in decisions'
    },
    {
      value: 'transparent',
      label: 'Transparent (Limited Input)',
      description: 'Clear policies and communication but limited employee participation',
      detail: 'Workers know what/why/how but no design input'
    },
    {
      value: 'disclosed',
      label: 'Disclosed but Opaque',
      description: 'Employees know monitoring exists but lack clarity on scope/usage',
      detail: 'Awareness without understanding or control'
    },
    {
      value: 'covert',
      label: 'Covert/Non-Disclosed',
      description: 'Undisclosed or hidden monitoring practices',
      detail: 'Severe procedural injustice and trust violation'
    }
  ];

  const orgCultureTypes = [
    {
      value: 'collaborative',
      label: 'High-Trust, Collaborative',
      description: 'Surveillance co-designed with employees as transparent performance system',
      detail: 'Strong psychological safety and participatory practices'
    },
    {
      value: 'supportive',
      label: 'High-Trust, Supportive',
      description: 'Monitoring framed as developmental feedback rather than control',
      detail: 'Relational foundations buffer surveillance impacts'
    },
    {
      value: 'transactional',
      label: 'Moderate-Trust, Transactional',
      description: 'Surveillance justified through performance management rationales',
      detail: 'Instrumental acceptance without deep trust'
    },
    {
      value: 'authoritarian',
      label: 'Low-Trust, Authoritarian',
      description: 'Top-down control with surveillance perceived as distrust signal',
      detail: 'Amplifies technostress and resistance'
    }
  ];

  const purposeTypes = [
    {
      value: 'developmental',
      label: 'Developmental',
      description: 'Coaching, training needs identification, employee growth support',
      detail: 'Highest perceived legitimacy'
    },
    {
      value: 'security',
      label: 'Security & Compliance',
      description: 'Asset protection, regulatory adherence, legal risk mitigation',
      detail: 'Generally accepted as legitimate'
    },
    {
      value: 'operational',
      label: 'Operational Optimization',
      description: 'Workflow improvement, resource allocation, system efficiency',
      detail: 'Neutral to moderately accepted'
    },
    {
      value: 'performance',
      label: 'Performance Management',
      description: 'Productivity metrics for evaluation and compensation decisions',
      detail: 'Mixed perceptions - depends on fairness'
    },
    {
      value: 'control',
      label: 'Control-Oriented',
      description: 'Compliance enforcement, discipline, preventing counterproductive behavior',
      detail: 'Violates professional norms - highest resistance'
    }
  ];

  const getImpactAssessment = () => {
    if (!autonomyLevel || !surveillanceType || !implementation || !orgCulture || !surveillancePurpose) {
      return null;
    }

    // Sophisticated impact calculation based on framework logic
    let severityScore = 0;
    let mechanisms = [];
    let violationType = '';

    // Dimension 1: Autonomy Level (primary moderator)
    const autonomyMultiplier = {
      low: 1,
      medium: 1.5,
      high: 2.5,
      ultra: 3
    }[autonomyLevel] || 1;

    // Dimension 2: Surveillance Type
    const surveillanceScore = {
      outcome: 1,
      mixed: 2,
      process: 3.5,
      pervasive: 5
    }[surveillanceType] || 1;

    // Dimension 3: Implementation Transparency
    const implementationScore = {
      participatory: 0.5,
      transparent: 1,
      disclosed: 2,
      covert: 3
    }[implementation] || 1;

    // Dimension 4: Organizational Culture
    const cultureScore = {
      collaborative: 0.5,
      supportive: 1,
      transactional: 1.5,
      authoritarian: 2.5
    }[orgCulture] || 1;

    // Dimension 5: Surveillance Purpose
    const purposeScore = {
      developmental: 0.5,
      security: 1,
      operational: 1.5,
      performance: 2,
      control: 3
    }[surveillancePurpose] || 1;

    // Calculate base severity (normalized to 0-20 scale)
    // Max possible raw score before multipliers: (5+3+2.5+3) * 3 * 1.3 * 1.2 = 52.65
    // We'll normalize by dividing by max and multiplying by 20
    let rawScore = (surveillanceScore + implementationScore + cultureScore + purposeScore) * autonomyMultiplier;

    // Determine CI violation type and mechanisms based on autonomy
    if (autonomyLevel === 'high' || autonomyLevel === 'ultra') {
      violationType = 'severe';
      mechanisms = [
        {
          name: 'Mechanism 1: Trust Erosion',
          description: 'Surveillance signals withdrawal of organizational trust, violating the psychological contract. High-autonomy workers who have historically been trusted to self-manage interpret monitoring as doubting their competence, commitment, or integrity.'
        },
        {
          name: 'Mechanism 2: Temporal Autonomy Erosion',
          description: 'Tracking systems erode workers\' control over when and how long to work on tasks. This disrupts ability to structure time according to task demands, creating fragmented work patterns and constant availability pressure.'
        },
        {
          name: 'Mechanism 3: Procedural Injustice',
          description: 'Surveillance imposed without consultation, justification, or voice violates procedural fairness norms. Particularly severe when contradicting the autonomy explicitly promised in job design or organizational rhetoric.'
        }
      ];

      // Adjust severity for high-autonomy specific factors
      if (surveillanceType === 'process' || surveillanceType === 'pervasive') {
        rawScore *= 1.3; // Autonomy-surveillance incongruence
      }
      if (orgCulture === 'collaborative' || orgCulture === 'supportive') {
        rawScore *= 1.2; // Trust culture × autonomy interaction (P3)
      }
    } else {
      violationType = 'moderate';
      mechanisms = [
        {
          name: 'Factor 1: Historical Precedent',
          description: 'Low-autonomy roles have historically involved direct supervision and performance measurement. Digital surveillance represents a continuation rather than disruption of established monitoring practices.'
        },
        {
          name: 'Factor 2: Role Clarity & Performance Metrics',
          description: 'Well-defined tasks and objective performance metrics mean surveillance provides transparent, quantifiable feedback. May be perceived as fair when metrics are clear and actionable.'
        },
        {
          name: 'Factor 3: Limited Privacy Expectations',
          description: 'Workers in low-autonomy positions often enter roles with constrained expectations about workplace privacy. Baseline threshold for CI violation is higher because monitoring aligns with pre-existing norms.'
        }
      ];

      // Normalized surveillance phenomenon - reduce severity for low autonomy
      // Low-autonomy workers have normalized expectations but still react negatively to poor implementation
      if (autonomyLevel === 'low') {
        rawScore *= 0.75; // Reduced impact but not eliminated
      }
    }

    // Normalize to 0-20 scale using autonomy-level-specific maximum
    // This ensures each autonomy level has meaningful score differentiation
    let maxForAutonomyLevel;
    if (autonomyLevel === 'low') {
      // Max for low: (5+3+2.5+3) * 1 * 0.75 = 10.125
      maxForAutonomyLevel = 10.125;
    } else if (autonomyLevel === 'medium') {
      // Max for medium: (5+3+2.5+3) * 1.5 = 20.25
      maxForAutonomyLevel = 20.25;
    } else if (autonomyLevel === 'high') {
      // Max for high: (5+3+2.5+3) * 2.5 * 1.3 * 1.2 = 52.65
      maxForAutonomyLevel = 52.65;
    } else { // ultra
      // Max for ultra: same as high
      maxForAutonomyLevel = 52.65;
    }

    severityScore = (rawScore / maxForAutonomyLevel) * 20;

    // Determine impact level based on normalized 0-20 scale
    // Thresholds calibrated for meaningful differentiation across autonomy levels
    // Best cases should be minimal (< 5), worst cases severe (> 15)
    let impactLevel, impactColor;
    if (severityScore < 5) {
      impactLevel = 'minimal';
      impactColor = 'green';
    } else if (severityScore < 10) {
      impactLevel = 'moderate';
      impactColor = 'yellow';
    } else if (severityScore < 15) {
      impactLevel = 'high';
      impactColor = 'orange';
    } else {
      impactLevel = 'severe';
      impactColor = 'red';
    }

    return {
      level: impactLevel,
      color: impactColor,
      score: severityScore.toFixed(1),
      violationType,
      mechanisms
    };
  };

  const getPredictedOutcomes = () => {
    const impact = getImpactAssessment();
    if (!impact) return null;

    const isHighAutonomy = autonomyLevel === 'high' || autonomyLevel === 'ultra';

    const outcomesByLevel: Record<string, any> = {
      minimal: {
        trust: 'Maintained or slightly reduced - baseline trust preserved',
        technostress: 'Low to minimal - within normal work stress range',
        performance: 'Stable or improved via constructive feedback mechanisms',
        satisfaction: 'No significant decline - may improve with clear metrics',
        turnover: 'No significant change in turnover intention',
        ciViolation: 'Minimal CI violation - monitoring aligns with role expectations',
        mechanisms: isHighAutonomy
            ? 'Transparency and participatory design partially mitigate trust concerns'
            : 'Fairness criteria met - surveillance perceived as contextually appropriate'
      },
      moderate: {
        trust: 'Moderately reduced - some erosion of organizational trust',
        technostress: 'Moderate levels - manageable but present stress responses',
        performance: 'Mixed outcomes - possible short-term productivity gains, long-term engagement concerns',
        satisfaction: 'Modest decline in job satisfaction',
        turnover: 'Slight increase in turnover intention - retention concerns emerging',
        ciViolation: 'Moderate CI violation - some departure from expected informational norms',
        mechanisms: isHighAutonomy
            ? 'Trust erosion and procedural injustice mechanisms activated but constrained'
            : 'Fairness evaluations critical - outcomes depend on implementation quality'
      },
      high: {
        trust: 'Significantly eroded - fundamental trust damage',
        technostress: 'High levels - techno-overload and techno-invasion present',
        performance: 'Likely decline - performative productivity replaces genuine creative work',
        satisfaction: 'Significant reduction in job satisfaction and organizational commitment',
        turnover: 'Increased turnover risk - talent retention challenges',
        ciViolation: 'Severe CI violation - major departure from professional norms',
        mechanisms: isHighAutonomy
            ? 'All three mechanisms fully activated: trust erosion + temporal autonomy loss + procedural injustice'
            : 'Fairness violations trigger resistance - monitoring exceeds normalized expectations'
      },
      severe: {
        trust: 'Fundamentally damaged - psychological contract breach',
        technostress: 'Severe - high risk of burnout and psychological distress',
        performance: 'Significant decline - creativity and innovation suppressed, reactance behaviors likely',
        satisfaction: 'Critical deterioration in satisfaction and organizational commitment',
        turnover: 'High turnover risk - retention crisis likely, top talent departure',
        ciViolation: 'Critical CI violation - complete breach of role-based informational contract',
        mechanisms: isHighAutonomy
            ? 'Autonomy-surveillance incongruence creates fundamental role incompatibility. All mechanisms at maximum severity.'
            : 'Excessive monitoring violates even normalized expectations. Dignity concerns emerge.'
      }
    };

    return outcomesByLevel[impact.level];
  };

  const getRecommendations = () => {
    const impact = getImpactAssessment();
    if (!impact) return [];

    const recommendations = [];
    const isHighAutonomy = autonomyLevel === 'high' || autonomyLevel === 'ultra';
    const isLowAutonomy = autonomyLevel === 'low';

    // Critical: High autonomy + process/pervasive surveillance
    if (isHighAutonomy && (surveillanceType === 'process' || surveillanceType === 'pervasive')) {
      recommendations.push({
        priority: 'critical',
        category: 'Fundamental Redesign Required',
        text: 'CRITICAL: Process-focused surveillance creates severe autonomy-surveillance incongruence for knowledge workers. Shift to outcome-based metrics to preserve professional discretion and prevent trust erosion.',
        evidence: 'Framework Proposition 4 (Role Incongruence) + Mechanism 2 (Temporal Autonomy Erosion)'
      });
    }

    // Critical: Any autonomy + pervasive surveillance
    if (surveillanceType === 'pervasive') {
      recommendations.push({
        priority: 'critical',
        category: 'Reduce Invasiveness',
        text: 'Pervasive monitoring (webcam/audio/biometric) creates critical CI violations across all autonomy levels. Immediate reduction in surveillance intensity required.',
        evidence: 'Dimension 2 (Surveillance Informational Focus) - highest invasiveness category'
      });
    }

    // Critical: Covert/non-transparent implementation
    if (implementation === 'covert') {
      recommendations.push({
        priority: 'critical',
        category: 'Transparency & Procedural Justice',
        text: 'Covert surveillance violates procedural justice fundamentally. Immediate disclosure required: communicate what is monitored, why, how data is used, and who has access.',
        evidence: 'Dimension 3 (Implementation Transparency) + Mechanism 3 (Procedural Injustice)'
      });
      recommendations.push({
        priority: 'high',
        category: 'Employee Participation',
        text: 'Implement participatory design process - involve employees in surveillance policy development to restore procedural fairness.',
        evidence: 'Section 5.2.1 (Implications for High-Autonomy Organizations)'
      });
    }

    // High: Control-oriented purpose
    if (surveillancePurpose === 'control') {
      recommendations.push({
        priority: 'high',
        category: 'Reframe Purpose',
        text: 'Control-oriented surveillance violates expectations of appropriate information flows. Reframe toward developmental or security purposes with legitimate business justification.',
        evidence: 'Dimension 5 (Surveillance Purpose and Rationale)'
      });
    }

    // High: Authoritarian culture amplifies negative effects
    if (orgCulture === 'authoritarian') {
      recommendations.push({
        priority: 'high',
        category: 'Organizational Culture',
        text: 'Low-trust authoritarian culture amplifies technostress and surveillance resistance. Invest in trust-building, supportive communication, and psychological safety.',
        evidence: 'Dimension 4 (Organizational Culture and Trust Climate) + P3 (Trust Culture Interaction)'
      });
    }

    // High: High autonomy needs special handling
    if (isHighAutonomy && (implementation !== 'participatory')) {
      recommendations.push({
        priority: 'high',
        category: 'Participatory Design',
        text: 'For knowledge workers, participatory surveillance design is essential (not optional). Involve employees in decisions about what to monitor, how to use data, and privacy protections.',
        evidence: 'Section 5.2.1 - Implementation transparency alone is insufficient for high-autonomy roles'
      });
    }

    // Medium: Moderate transparency can be improved
    if (implementation === 'disclosed' || implementation === 'transparent') {
      recommendations.push({
        priority: 'medium',
        category: 'Enhance Transparency',
        text: 'Increase transparency beyond basic disclosure: provide algorithmic explanations, data access rights, and clear justification tied to legitimate business needs.',
        evidence: 'Recent research on algorithmic HR (Jabagi et al., 2025; Semujanga et al., 2024)'
      });
    }

    // Medium: General improvements for moderate/high impact
    if (impact.level === 'high' || impact.level === 'severe') {
      recommendations.push({
        priority: 'high',
        category: 'Monitoring & Feedback',
        text: 'Establish formal channels for employee feedback on surveillance practices. Conduct regular audits of data usage, retention policies, and compliance with stated purposes.',
        evidence: 'Section 5.4 (Future Research Directions) - intervention studies'
      });

      if (isHighAutonomy) {
        recommendations.push({
          priority: 'medium',
          category: 'Alternative Approaches',
          text: 'Consider goal alignment and trust-building practices instead of process monitoring. Invest in outcome definition that preserves autonomy that attracted talent.',
          evidence: 'Section 5.2.1 - Remote work contexts for knowledge workers'
        });
      }
    }

    // Positive: Well-aligned configuration
    if (impact.level === 'minimal' && isLowAutonomy &&
        (surveillanceType === 'outcome' || surveillanceType === 'mixed') &&
        (implementation === 'participatory' || implementation === 'transparent')) {
      recommendations.push({
        priority: 'positive',
        category: 'Current Approach Validated',
        text: 'Current surveillance configuration is well-aligned with role expectations and minimizes CI violations. Continue monitoring fairness perceptions and providing actionable feedback.',
        evidence: 'Proposition 5 (Normalized Surveillance) + Factor 2 (Role Clarity & Performance Metrics)'
      });
    }

    // Low autonomy specific guidance
    if (isLowAutonomy && implementation === 'participatory') {
      recommendations.push({
        priority: 'positive',
        category: 'Low-Autonomy Best Practice',
        text: 'For low-autonomy roles, maintain focus on three fairness factors: transparency (what/why), consistency (uniform application), and legitimate purpose. Current approach meets these criteria.',
        evidence: 'Section 5.2.2 (Implications for Low-Autonomy Organizations)'
      });
    }

    // Policy recommendations for severe cases
    if (impact.level === 'severe') {
      recommendations.push({
        priority: 'critical',
        category: 'Regulatory Compliance Risk',
        text: 'Current configuration may violate GDPR principles (data minimization, purpose limitation) and creates significant employee relations risk. Consider legal review and ethics assessment.',
        evidence: 'Section 5.2.3 (Implications for Policymakers) - GDPR alignment'
      });
    }

    return recommendations;
  };

  const handleAssess = () => {
    setShowResults(true);
  };

  const handleReset = () => {
    setAutonomyLevel('');
    setSurveillanceType('');
    setImplementation('');
    setOrgCulture('');
    setSurveillancePurpose('');
    setShowResults(false);
  };

  const impact = getImpactAssessment();
  const outcomes = getPredictedOutcomes();
  const recommendations = getRecommendations();

  const getImpactColor = (color: string) => {
    const colors: Record<string, string> = {
      green: 'bg-green-100 border-green-400 text-green-800',
      yellow: 'bg-yellow-100 border-yellow-400 text-yellow-800',
      orange: 'bg-orange-100 border-orange-400 text-orange-800',
      red: 'bg-red-100 border-red-400 text-red-800'
    };
    return colors[color];
  };

  const getImpactIcon = (color: string) => {
    if (color === 'green') return <CheckCircle className="w-8 h-8" />;
    if (color === 'yellow' || color === 'orange') return <AlertCircle className="w-8 h-8" />;
    return <XCircle className="w-8 h-8" />;
  };

  const allDimensionsSelected = autonomyLevel && surveillanceType && implementation && orgCulture && surveillancePurpose;

  return (
      <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="flex items-start gap-4 mb-4">
            <BookOpen className="w-10 h-10 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Autonomy-Surveillance Survey & Evaluation Tool (ASSET)
              </h1>
              <p className="text-gray-600 mb-2">
                Interactive implementation of the Autonomy-Surveillance Conceptual Framework
              </p>
              <p className="text-sm text-gray-500">
                Nikodinovski, A., Mirčetić, V., & Karabašević, D. (2025). <em>Digital Panopticon: How Remote Work Monitoring Shapes Employee Behavior and Motivation</em>.
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">About This Framework</p>
              <p>This tool operationalizes a theoretical framework predicting differential psychological impacts of digital workplace surveillance based on five key dimensions. The framework integrates Foucault's theory of disciplinary power, Nissenbaum's contextual integrity framework, and job design theory to explain <strong>why, when, and for whom</strong> surveillance violates workplace norms.</p>
              <p className="mt-2"><strong>Key Insight:</strong> Job autonomy moderates surveillance impact severity. High-autonomy workers experience surveillance as a severe contextual integrity violation (triggering trust erosion, temporal autonomy loss, and procedural injustice), while low-autonomy workers evaluate surveillance through fairness lenses within normalized monitoring expectations.</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Dimension 1: Autonomy Level */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-2">
                Dimension 1: Job Autonomy Level <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-3">Primary moderator - determines baseline informational norms and CI violation severity</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {autonomyLevels.map((level) => (
                    <button
                        key={level.value}
                        onClick={() => setAutonomyLevel(level.value)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                            autonomyLevel === level.value
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <div className="font-semibold text-gray-900">{level.label}</div>
                      <div className="text-sm text-gray-700 mt-1">{level.description}</div>
                      <div className="text-xs text-gray-500 mt-1">{level.examples}</div>
                    </button>
                ))}
              </div>
            </div>

            {/* Dimension 2: Surveillance Type */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-2">
                Dimension 2: Surveillance Informational Focus <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-3">What information is collected and how invasively (EPM invasiveness dimension)</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {surveillanceTypes.map((type) => (
                    <button
                        key={type.value}
                        onClick={() => setSurveillanceType(type.value)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                            surveillanceType === type.value
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <div className="font-semibold text-gray-900">{type.label}</div>
                      <div className="text-sm text-gray-700 mt-1">{type.description}</div>
                      <div className="text-xs text-gray-500 mt-1">{type.impact}</div>
                    </button>
                ))}
              </div>
            </div>

            {/* Dimension 3: Implementation Transparency */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-2">
                Dimension 3: Implementation Transparency <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-3">Employee awareness, participation, and procedural justice in surveillance design</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {implementationTypes.map((impl) => (
                    <button
                        key={impl.value}
                        onClick={() => setImplementation(impl.value)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                            implementation === impl.value
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <div className="font-semibold text-gray-900">{impl.label}</div>
                      <div className="text-sm text-gray-700 mt-1">{impl.description}</div>
                      <div className="text-xs text-gray-500 mt-1">{impl.detail}</div>
                    </button>
                ))}
              </div>
            </div>

            {/* Dimension 4: Organizational Culture */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-2">
                Dimension 4: Organizational Culture & Trust Climate <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-3">Baseline trust levels and cultural context that shapes surveillance interpretation</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {orgCultureTypes.map((culture) => (
                    <button
                        key={culture.value}
                        onClick={() => setOrgCulture(culture.value)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                            orgCulture === culture.value
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <div className="font-semibold text-gray-900">{culture.label}</div>
                      <div className="text-sm text-gray-700 mt-1">{culture.description}</div>
                      <div className="text-xs text-gray-500 mt-1">{culture.detail}</div>
                    </button>
                ))}
              </div>
            </div>

            {/* Dimension 5: Surveillance Purpose */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-2">
                Dimension 5: Surveillance Purpose & Rationale <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-3">Stated organizational purpose - shapes legitimacy perceptions and fairness evaluations</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {purposeTypes.map((purpose) => (
                    <button
                        key={purpose.value}
                        onClick={() => setSurveillancePurpose(purpose.value)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                            surveillancePurpose === purpose.value
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <div className="font-semibold text-gray-900">{purpose.label}</div>
                      <div className="text-sm text-gray-700 mt-1">{purpose.description}</div>
                      <div className="text-xs text-gray-500 mt-1">{purpose.detail}</div>
                    </button>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <button
                  onClick={handleAssess}
                  disabled={!allDimensionsSelected}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <AlertCircle className="w-5 h-5" />
                Generate Assessment
              </button>
              {showResults && (
                  <button
                      onClick={handleReset}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                  >
                    Reset All
                  </button>
              )}
            </div>
          </div>
        </div>

        {/* Results Section */}
        {showResults && impact && (
            <div className="space-y-6">
              {/* Impact Level Card */}
              <div className={`rounded-lg border-2 p-6 ${getImpactColor(impact.color)}`}>
                <div className="flex items-center gap-4 mb-4">
                  {getImpactIcon(impact.color)}
                  <div>
                    <h2 className="text-2xl font-bold capitalize">{impact.level} Psychological Impact</h2>
                    <p className="text-sm mt-1">Predicted employee well-being and trust burden (Severity Score: {impact.score}/20)</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-white/50 rounded border border-current/20">
                  <p className="font-semibold text-sm">Contextual Integrity Violation Type:</p>
                  <p className="text-sm mt-1">
                    {impact.violationType === 'severe'
                        ? 'Severe CI Violation - Surveillance fundamentally contradicts role-based informational norms, disrupting established expectations of professional discretion and trust.'
                        : 'Moderate CI Violation - Surveillance aligns with normalized monitoring expectations but outcomes depend on fairness of implementation.'}
                  </p>
                </div>
              </div>

              {/* Mechanisms/Factors Explanation */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {impact.violationType === 'severe' ? 'Active Psychological Mechanisms' : 'Normalization Factors'}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {impact.violationType === 'severe'
                      ? 'For high-autonomy workers, surveillance triggers three parallel mechanisms that converge to produce negative outcomes:'
                      : 'For low-autonomy workers, three factors contribute to normalized surveillance perceptions:'}
                </p>
                <div className="space-y-4">
                  {impact.mechanisms.map((mech, idx) => (
                      <div key={idx} className="border-l-4 border-blue-500 pl-4 py-2">
                        <div className="font-semibold text-gray-900">{mech.name}</div>
                        <div className="text-sm text-gray-700 mt-1">{mech.description}</div>
                      </div>
                  ))}
                </div>
              </div>

              {/* Predicted Outcomes */}
              {outcomes && (
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Predicted Organizational Outcomes</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="border-l-4 border-red-500 pl-4 py-2">
                        <div className="font-semibold text-gray-700">Organizational Trust</div>
                        <div className="text-sm text-gray-600 mt-1">{outcomes.trust}</div>
                      </div>
                      <div className="border-l-4 border-purple-500 pl-4 py-2">
                        <div className="font-semibold text-gray-700">Technostress Level</div>
                        <div className="text-sm text-gray-600 mt-1">{outcomes.technostress}</div>
                      </div>
                      <div className="border-l-4 border-green-500 pl-4 py-2">
                        <div className="font-semibold text-gray-700">Performance Impact</div>
                        <div className="text-sm text-gray-600 mt-1">{outcomes.performance}</div>
                      </div>
                      <div className="border-l-4 border-yellow-500 pl-4 py-2">
                        <div className="font-semibold text-gray-700">Job Satisfaction</div>
                        <div className="text-sm text-gray-600 mt-1">{outcomes.satisfaction}</div>
                      </div>
                      <div className="border-l-4 border-orange-500 pl-4 py-2">
                        <div className="font-semibold text-gray-700">Turnover Risk</div>
                        <div className="text-sm text-gray-600 mt-1">{outcomes.turnover}</div>
                      </div>
                      <div className="border-l-4 border-blue-500 pl-4 py-2">
                        <div className="font-semibold text-gray-700">CI Violation Summary</div>
                        <div className="text-sm text-gray-600 mt-1">{outcomes.ciViolation}</div>
                      </div>
                    </div>
                    {outcomes.mechanisms && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="text-sm text-gray-700">
                            <strong>Mediating Pathway:</strong> {outcomes.mechanisms}
                          </div>
                        </div>
                    )}
                  </div>
              )}

              {/* Evidence-Based Recommendations */}
              {recommendations.length > 0 && (
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Evidence-Based Recommendations</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Actionable guidance derived from framework propositions, mechanisms, and empirical literature synthesis
                    </p>
                    <div className="space-y-3">
                      {recommendations.map((rec, idx) => {
                        const priorityConfig: Record<string, { color: string; label: string; icon: any }> = {
                          critical: {
                            color: 'border-red-500 bg-red-50 text-red-900',
                            label: 'CRITICAL ACTION REQUIRED',
                            icon: <XCircle className="w-5 h-5" />
                          },
                          high: {
                            color: 'border-orange-500 bg-orange-50 text-orange-900',
                            label: 'HIGH PRIORITY',
                            icon: <AlertCircle className="w-5 h-5" />
                          },
                          medium: {
                            color: 'border-yellow-500 bg-yellow-50 text-yellow-900',
                            label: 'MEDIUM PRIORITY',
                            icon: <Info className="w-5 h-5" />
                          },
                          positive: {
                            color: 'border-green-500 bg-green-50 text-green-900',
                            label: 'POSITIVE VALIDATION',
                            icon: <CheckCircle className="w-5 h-5" />
                          }
                        };
                        const config = priorityConfig[rec.priority];
                        return (
                            <div key={idx} className={`border-l-4 p-4 rounded ${config.color}`}>
                              <div className="flex items-center gap-2 mb-2">
                                {config.icon}
                                <div>
                                  <div className="font-bold text-xs">{config.label}</div>
                                  <div className="font-semibold text-sm">{rec.category}</div>
                                </div>
                              </div>
                              <div className="text-sm mb-2">{rec.text}</div>
                              <div className="text-xs opacity-75 italic">
                                <strong>Evidence:</strong> {rec.evidence}
                              </div>
                            </div>
                        );
                      })}
                    </div>
                  </div>
              )}

              {/* Framework Reference & Propositions */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-6 border border-gray-200">
                <h4 className="font-bold text-gray-900 mb-3">Theoretical Framework Logic</h4>
                <div className="text-sm text-gray-700 space-y-2">
                  <p>
                    <strong>Core Proposition (P1 - Main Effect):</strong> Workplace surveillance has stronger negative effects on job satisfaction, organizational trust, and psychological well-being for high-autonomy workers than for low-autonomy workers.
                  </p>
                  <p>
                    <strong>Mechanism:</strong> The severity of Contextual Integrity violation is proportional to job autonomy level. High-autonomy workers experience surveillance as betraying implicit professional contracts (discretion over process), while low-autonomy workers evaluate surveillance through fairness and implementation quality lenses.
                  </p>
                  <p className="pt-2 border-t border-gray-300">
                    <strong>Additional Testable Propositions:</strong>
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><strong>P2 (Transparency Moderation):</strong> Transparency moderates impacts more strongly for low-autonomy workers than high-autonomy workers</li>
                    <li><strong>P3 (Trust Culture Interaction):</strong> High-autonomy workers in high-trust cultures experience most severe reactions; low-autonomy workers in low-trust cultures experience least severe</li>
                    <li><strong>P4 (Surveillance Type):</strong> Informational surveillance produces stronger reactions than behavioral surveillance for high-autonomy workers</li>
                    <li><strong>P5 (Differential Mediation):</strong> High-autonomy: trust erosion + procedural injustice pathway; Low-autonomy: fairness evaluation pathway</li>
                  </ul>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-300 text-xs text-gray-600">
                  <p><strong>Note:</strong> This framework is conceptual and requires empirical validation. Predictions are based on theoretical synthesis of Foucault (disciplinary power), Nissenbaum (contextual integrity), and Hackman & Oldham (job design theory).</p>
                </div>
              </div>
            </div>
        )}
      </div>
  );
};

export default SurveillanceMatrix;